import React, { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, X, KeyRound } from "lucide-react";
import { adminChangePassword } from "../lib/api";

const RULES = [
  { key: "len", label: "At least 12 characters", test: (v) => v.length >= 12 },
  { key: "upper", label: "One uppercase letter", test: (v) => /[A-Z]/.test(v) },
  { key: "lower", label: "One lowercase letter", test: (v) => /[a-z]/.test(v) },
  { key: "num", label: "One number", test: (v) => /\d/.test(v) },
  { key: "special", label: "One special character", test: (v) => /[^A-Za-z0-9]/.test(v) },
];

function PwField({ id, label, value, onChange, show, onToggle, testid }) {
  return (
    <div>
      <label htmlFor={id} className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-3">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={show ? "text" : "password"}
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/25 px-4 py-3 pr-11 text-[#1C110C] text-sm focus-gold"
          autoComplete="off"
        />
        <button
          type="button"
          onClick={onToggle}
          data-testid={`${testid}-toggle`}
          aria-label={show ? "Hide password" : "Show password"}
          className="absolute inset-y-0 right-0 px-3 flex items-center text-[#7A6B5F] hover:text-[#6E1F2C]"
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );
}

export const ChangePasswordModal = ({ onClose, onSuccess }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const failedRules = RULES.filter((r) => !r.test(next));
  const sameAsCurrent = next.length > 0 && next === current;
  const confirmMismatch = confirm.length > 0 && confirm !== next;
  const canSubmit =
    current.length > 0 &&
    failedRules.length === 0 &&
    !sameAsCurrent &&
    confirm === next &&
    next.length > 0 &&
    !loading;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      await adminChangePassword(current, next);
      toast.success("Password changed successfully. Please sign in again.");
      onSuccess();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not change password. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-change-password-modal"
      className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-[#1C110C]/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-[#FAF5EC] border border-[#6E1F2C]/20 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#6E1F2C]/15">
          <p className="inline-flex items-center gap-2 font-serif-display tracking-[0.2em] uppercase text-sm">
            <KeyRound size={15} className="text-[#6E1F2C]" /> Change Password
          </p>
          <button
            type="button"
            onClick={onClose}
            data-testid="admin-change-password-close"
            aria-label="Close"
            className="text-[#7A6B5F] hover:text-[#6E1F2C]"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-5">
          <PwField id="cp-current" label="Current password" value={current} onChange={setCurrent}
            show={showCurrent} onToggle={() => setShowCurrent((s) => !s)} testid="cp-current" />

          <PwField id="cp-new" label="New password" value={next} onChange={setNext}
            show={showNext} onToggle={() => setShowNext((s) => !s)} testid="cp-new" />

          {next.length > 0 && (
            <ul className="space-y-1" data-testid="cp-policy">
              {RULES.map((r) => {
                const ok = r.test(next);
                return (
                  <li key={r.key} className={`text-xs flex items-center gap-2 ${ok ? "text-green-700" : "text-[#9A8A7F]"}`}>
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${ok ? "bg-green-600" : "bg-[#9A8A7F]/50"}`} />
                    {r.label}
                  </li>
                );
              })}
            </ul>
          )}
          {sameAsCurrent && (
            <p className="text-xs text-red-700" data-testid="cp-error-same">
              New password must be different from the current password.
            </p>
          )}

          <PwField id="cp-confirm" label="Confirm new password" value={confirm} onChange={setConfirm}
            show={showConfirm} onToggle={() => setShowConfirm((s) => !s)} testid="cp-confirm" />
          {confirmMismatch && (
            <p className="text-xs text-red-700" data-testid="cp-error-mismatch">
              Passwords do not match.
            </p>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              data-testid="admin-change-password-cancel"
              className="flex-1 border border-[#6E1F2C]/25 text-[#7A6B5F] px-4 py-3 text-[10px] tracking-[0.28em] uppercase hover:text-[#1C110C] hover:border-[#6E1F2C]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="admin-change-password-submit"
              className="flex-1 bg-[#6E1F2C] text-[#FAF5EC] px-4 py-3 text-[10px] tracking-[0.28em] uppercase hover:bg-[#56151F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Saving..." : "Change Password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
